import { CobaltPageHeader } from '@cae/cobalt-react/components/page-header';
import { CobaltSidePanelContainer } from '@cae/cobalt-react/components/side-panel';
import { useAuthenticatedUser } from '@contexts/auth-provider';
import { useHasPermission } from '@contexts/permissions-provider';
import { ErrorBanner } from '@features/home/components/banners/error-banner/ErrorBanner';
import { MaintenanceBanner } from '@features/home/components/banners/maintenance-banner/MaintenanceBanner';
import { ForYou } from '@features/home/components/for-you/ForYou';
import { QuickLinks } from '@features/home/components/quick-links/QuickLinks';
import { PastDueInvoicesPanel } from '@features/home/components/your-alerts/panels/past-due-invoices-panel/PastDueInvoicesPanel';
import { ReservationsDashboardAlertsPanels } from '@features/home/components/your-alerts/panels/reservations-dashboard-alerts-panels/ReservationsDashboardAlertsPanels';
import { AlertTile } from '@features/home/components/your-alerts/tiles/alert-tile/AlertTile';
import { ReservationsAlertsTiles } from '@features/home/components/your-alerts/tiles/reservations-alerts-tiles/ReservationsAlertsTiles';
import { YourAlerts } from '@features/home/components/your-alerts/YourAlerts';
import { useDashboardAlertsState } from '@features/home/hooks/use-dashboard-alerts-state/useDashboardAlertsState';
import { InvoiceDetailPanel } from '@features/invoices/components/detail-panel/InvoiceDetailPanel';
import { ContentLoader } from '@shared/components/content-loader/ContentLoader';
import { useFlags } from 'launchdarkly-react-client-sdk';
import * as React from 'react';

import styles from './ManageHomeContent.module.css';

const ManageHomeContent = React.memo((): React.ReactElement => {
  const { firstName } = useAuthenticatedUser();
  const canViewForYou = useHasPermission('HelpBasic_View_Any');
  const canViewReservations = useHasPermission('Reservations_View_Org');
  const canViewRecords = useHasPermission('Records_View_Org');
  const canViewReservationAlerts = canViewReservations || canViewRecords;
  const { manageHomeAlerts } = useFlags();
  const isManageHomeAlertsEnabled = manageHomeAlerts === true;
  const alerts = useDashboardAlertsState({
    enabled: isManageHomeAlertsEnabled,
  });
  const [dismissedError, setDismissedError] = React.useState<boolean>(false);

  React.useEffect((): void => {
    setDismissedError(false);
  }, [alerts.alertError.type]);

  const handleErrorDismiss = React.useCallback((): void => {
    setDismissedError(true);
  }, []);

  let sidePanelContent: React.ReactNode;

  if (isManageHomeAlertsEnabled && alerts.activePanel === 'invoice-list') {
    sidePanelContent = (
      <PastDueInvoicesPanel
        invoices={alerts.pastDueInvoices}
        isOpen
        isLoading={alerts.pastDueIsLoading}
        isError={!!alerts.pastDueError}
        onClose={alerts.closePanel}
        onInvoiceClick={alerts.handleInvoiceClick}
      />
    );
  } else if (
    isManageHomeAlertsEnabled &&
    alerts.activePanel === 'invoice-detail'
  ) {
    sidePanelContent = alerts.selectedInvoiceId ? (
      <InvoiceDetailPanel
        accountReceivableId={alerts.selectedInvoiceId}
        onClose={alerts.handleDetailClose}
      />
    ) : null;
  } else if (isManageHomeAlertsEnabled && alerts.activePanel === 'mfe') {
    sidePanelContent = (
      <ReservationsDashboardAlertsPanels
        isOpen
        activeAlertType={alerts.selectedMFEAlertType}
        onClose={alerts.closeMFEPanel}
      />
    );
  }

  return (
    <CobaltSidePanelContainer
      className={styles.contentAreaWrapper}
      data-testid="manage-home-content"
      content={
        <div className={styles.contentArea}>
          <div className={styles.bannersWrapper}>
            <MaintenanceBanner />
            {isManageHomeAlertsEnabled &&
              alerts.alertError.type &&
              !dismissedError && (
                <ErrorBanner
                  message="Something went wrong while loading some data. Please try refreshing the page."
                  onDismiss={handleErrorDismiss}
                />
              )}
          </div>
          <CobaltPageHeader
            title={`Hi, ${firstName}`}
            className={styles.manageHomeContentHeader}
          />

          <div className={styles.manageHomeContentSection}>
            {isManageHomeAlertsEnabled && alerts.isLoading && (
              <ContentLoader label="Getting things ready…" />
            )}

            <div className={styles.manageHomeContent}>
              <QuickLinks />
              {isManageHomeAlertsEnabled && alerts.showAlertsSection && (
                <YourAlerts>
                  {alerts.hasInvoiceAlerts && !alerts.invoiceError && (
                    <AlertTile
                      count={alerts.pastDueCount}
                      label="Past due invoices"
                      variant="danger"
                      onClick={alerts.openPanel}
                    />
                  )}
                  {canViewReservationAlerts && (
                    <ReservationsAlertsTiles
                      onAlertSelect={alerts.openMFEPanel}
                      onStateChange={alerts.setMFEAlertState}
                    />
                  )}
                </YourAlerts>
              )}
              {isManageHomeAlertsEnabled && !alerts.showAlertsSection && canViewReservationAlerts && (
                <ReservationsAlertsTiles
                  onAlertSelect={alerts.openMFEPanel}
                  onStateChange={alerts.setMFEAlertState}
                />
              )}
              {canViewForYou && <ForYou />}
            </div>
          </div>
        </div>
      }
      sidePanel={sidePanelContent}
    />
  );
});
ManageHomeContent.displayName = 'ManageHomeContent';

export { ManageHomeContent };
